import { Link } from "react-router-dom";
import type { ReactNode } from "react";
import "./_auth.css";

/*
 * Shared two-column auth shell.
 *
 * Desktop: dark teal-deep brand panel on the left (with eyebrow,
 * pull-quote, citation, and a feature list at the bottom). Form
 * panel on the right.
 *
 * Mobile (<900px): brand panel hidden, form panel fills the screen.
 *
 * Direct port of `_auth.css` semantics — every page in the bundle's
 * auth/ folder uses this shell except profile.html, which has its
 * own variant (`<ProfileShell>` below).
 */

export const LOGO_SRC = "/wot-logo-512.png";

interface Feature {
  icon: ReactNode;
  label: string;
}

export interface AuthShellProps {
  brand: {
    eyebrow: string;
    quote: ReactNode;
    cite?: string;
    features?: Feature[];
  };
  topRight?: ReactNode;
  children: ReactNode;
  /** Footer text under the form (sign-up/sign-in cross-link, etc.). */
  footer?: ReactNode;
}

export const AuthShell = ({ brand, topRight, children, footer }: AuthShellProps) => (
  <div className="auth-page">
    <div className="auth-shell">
      <aside className="brand-panel">
        <div className="brand-glow" />
        <div className="brand-glow lower" />

        <div className="brand-header">
          <div className="owl-avatar mustard"><img src={LOGO_SRC} alt="Wise Owl Talks" /></div>
          <div className="wordmark">Wise<span>Owl</span> Talks</div>
        </div>

        <div className="brand-middle">
          <div className="brand-eyebrow">{brand.eyebrow}</div>
          <p className="brand-quote">{brand.quote}</p>
          {brand.cite && <div className="brand-cite">{brand.cite}</div>}
        </div>

        {brand.features && (
          <div className="brand-footer">
            {brand.features.map((f, i) => (
              <div key={i} className="feat">{f.icon}{f.label}</div>
            ))}
          </div>
        )}
      </aside>

      <section className="form-panel">
        <div className="form-top">
          <Link className="lockup" to="/">
            <div className="owl-avatar sm"><img src={LOGO_SRC} alt="" /></div>
            <div className="wordmark dark" style={{ fontSize: 17 }}>Wise<span>Owl</span> Talks</div>
          </Link>
          {topRight}
        </div>

        <div className="form-inner">
          {children}
        </div>

        {footer && <div className="form-footer">{footer}</div>}
      </section>
    </div>
  </div>
);

/* ── Brand-panel decorative icons ─────────────────────
   Reused across multiple pages. Stroke uses a fixed
   soft-cream rgba to mirror the source CSS exactly. */
const FEAT_STROKE = "rgb(250 246 238 / 0.65)";

export const FeatIcon = {
  lock: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M5 11V8a7 7 0 0 1 14 0v3M4 11h16v9H4v-9z" stroke={FEAT_STROKE} strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  ),
  flame: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M12 3s5 5 5 10a5 5 0 1 1-10 0c0-2 1-3 1-3s.5 1.5 1.5 1.5C9.5 11 8 9 9 6c1 1 1.5 2.5 3 3 0-2-1-4 0-6z" stroke={FEAT_STROKE} strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  ),
  leaf: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M20 4c0 9-6 15-15 15 0-9 6-15 15-15z" stroke={FEAT_STROKE} strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  ),
  spark: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5L18 18M18 6l-2.5 2.5M8.5 15.5L6 18" stroke={FEAT_STROKE} strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
  book: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M5 4h10a3 3 0 0 1 3 3v13H8a3 3 0 0 1-3-3V4z" stroke={FEAT_STROKE} strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M5 17a3 3 0 0 1 3-3h10" stroke={FEAT_STROKE} strokeWidth="1.6" />
    </svg>
  ),
  chat: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M4 5h16v11H8l-4 4V5z" stroke={FEAT_STROKE} strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  ),
  clock: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="8" stroke={FEAT_STROKE} strokeWidth="1.6" />
      <path d="M12 8v4l3 2" stroke={FEAT_STROKE} strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
  mail: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M4 5h16v14H4V5z" stroke={FEAT_STROKE} strokeWidth="1.6" />
      <path d="M4 7l8 6 8-6" stroke={FEAT_STROKE} strokeWidth="1.6" />
    </svg>
  ),
};

/* ── Reusable inline SVGs (form panel) ─────────────── */
export const SvgArrow = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
export const SvgBack = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
export const SvgSend = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M4 12l16-8-6 16-3-6-7-2z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
  </svg>
);
export const SvgEye = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);
export const SvgGoogle = (
  <svg width="18" height="18" viewBox="0 0 48 48">
    <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z" />
    <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z" />
    <path fill="#FBBC05" d="M11.69 28.18c-.44-1.32-.69-2.73-.69-4.18s.25-2.86.69-4.18v-5.7H4.34A21.99 21.99 0 0 0 2 24c0 3.55.85 6.91 2.34 9.88l7.35-5.7z" />
    <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.07 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7C13.42 14.62 18.27 10.75 24 10.75z" />
  </svg>
);
